/**
 * The signed-in application frame, on the shadcn/ui sidebar-07 pattern: a
 * collapsible icon rail with a project switcher above its navigation and a
 * user menu below it, and a top bar with the sidebar trigger, a breadcrumb,
 * search and the authority's state.
 *
 * The markup is the kit's contract (`data-slot`, `ui-*` classes); behaviour is
 * attached afterwards by `chrome.ts` from the kit's primitives. Every list in
 * here is a keyed `repeat`, so a render never replaces a node a primitive
 * holds.
 */

import type { TemplateInstance } from 'hydrogen/runtime';
import { html, repeat } from 'hydrogen/runtime';

import type { SurfaceSnapshot } from '../generated/surface.js';
import { GLYPH, Icon } from './kit.js';
import {
  currentProject,
  NAV,
  navGlyph,
  type NavIcon,
  parseProjectPath,
  PROFILE_PATH,
  PROJECT_TABS,
  projectHref,
  PROJECTS_PATH,
  REPLAY_PATH,
  SETTINGS_PATH,
} from './nav.js';
import { MatrixLogo } from './public-brand.js';
import { LANDING_PATH } from './public-paths.js';
import { authorityState, authorityStateLabel, short, type AuthorityState } from './product.js';
import { projectEntries, type ProjectDirectory } from './project-directory.js';
import type { SignedInAccount } from './account-session.js';
import { DataUnavailable, NO_PROJECT_MESSAGE } from './states.js';
import type { SurfaceConnectionState } from './surface-connection.js';

export type ShellOptions = {
  path: string;
  snapshot: SurfaceSnapshot | null;
  /** Why the project's data could not be read, when it could not. */
  error: string | null;
  connectionState: SurfaceConnectionState;
  body: TemplateInstance;
  /** Who the Worker says this browser is; absent until the session answers. */
  account?: SignedInAccount | null;
  /** The account's projects, which the switcher lists. */
  directory?: ProjectDirectory;
  /** The project's data could not be read (it failed, or has not answered in time). */
  unavailable?: boolean;
};

/** The header identity of a person, rather than of an agent. */
export function accountIdentity(account: SignedInAccount): { name: string; email: string; initials: string } {
  const name = account.name.trim().length > 0 ? account.name.trim() : account.email;
  const initials = name
    .split(/[\s@._-]+/)
    .filter((part) => part.length > 0)
    .slice(0, 2)
    .map((part) => part[0]?.toUpperCase() ?? '')
    .join('');
  return { name, email: account.email, initials: initials.length > 0 ? initials : 'MX' };
}

/** One destination in the sidebar. */
type NavRow = { key: string; href: string; label: string; icon: NavIcon; active: boolean };

/**
 * What the current address highlights: a global destination by its path, a
 * project route by its tab (a change highlights Changes, history highlights
 * Code), and a replay receipt the Checks tab it was opened from.
 */
function activeKey(path: string): string {
  const route = parseProjectPath(path);
  // Another project's address is not found; nothing in this project's tabs is where the reader is.
  if (route) return route.project === currentProject() ? `tab:${route.tab}` : path;
  if (path === REPLAY_PATH) return 'tab:checks';
  return path;
}

function globalRows(path: string): NavRow[] {
  const active = activeKey(path);
  return NAV.filter((item) => item.sidebar).map((item) => ({
    key: item.href,
    href: item.href,
    label: item.label,
    icon: item.icon,
    active: item.href === active,
  }));
}

function projectRows(path: string): NavRow[] {
  const active = activeKey(path);
  if (currentProject() === null) return [];
  return PROJECT_TABS.map((item) => ({
    key: `tab:${item.tab}`,
    href: projectHref(item.tab),
    label: item.label,
    icon: item.icon,
    active: `tab:${item.tab}` === active,
  }));
}

function MenuLink(row: NavRow): TemplateInstance {
  return html`
    <li class="ui-sidebar-menu-item" data-slot="sidebar-menu-item">
      <a
        class="ui-sidebar-menu-button"
        data-slot="sidebar-menu-button"
        data-size="default"
        data-active="${String(row.active)}"
        aria-current="${row.active ? 'page' : undefined}"
        href="${row.href}"
        data-nav="${row.href}"
        title="${row.label}"
      >
        ${Icon(navGlyph(row.icon))}<span>${row.label}</span>
      </a>
    </li>
  `;
}

/** `owner/repo` as its two halves; a scope with no slash is all name. */
function scopeParts(scope: string): { owner: string; name: string } {
  const slash = scope.indexOf('/');
  return slash < 0 ? { owner: '', name: scope } : { owner: scope.slice(0, slash), name: scope.slice(slash + 1) };
}

function ProjectItem(entry: { scope: string; role: string | null; current: boolean }): TemplateInstance {
  const parts = scopeParts(entry.scope);
  return html`
    <div
      class="ui-dropdown-menu-item mx-menu-project"
      data-slot="dropdown-menu-item"
      role="menuitem"
      tabindex="-1"
      data-href="${projectHref('overview', {}, entry.scope)}"
      data-current="${String(entry.current)}"
      title="${entry.scope}"
    >
      <span class="mx-menu-project__text">
        <span class="mx-menu-project__name">${parts.name}</span>
        <span class="mx-menu-project__owner">${parts.owner}${entry.role === null ? '' : ` · ${entry.role}`}</span>
      </span>
      ${entry.current ? html`<svg class="mx-icon mx-menu-project__check" viewBox="0 0 16 16" aria-hidden="true"><path d="M3 8.5 6.5 12 13 4.5"></path></svg>` : html``}
    </div>
  `;
}

/** The project switcher: the name in full, and every project the account may open in its menu. */
function ProjectSwitcher(options: {
  snapshot: SurfaceSnapshot | null;
  directory: ProjectDirectory;
}): TemplateInstance {
  const project = currentProject();
  const parts = project === null ? null : scopeParts(project);
  const entries = projectEntries(options.directory, options.snapshot).map((entry) => ({
    scope: entry.scope,
    role: entry.role,
    current: entry.scope === project,
  }));
  return html`
    <div class="ui-dropdown-menu" data-slot="dropdown-menu" data-menu="project" style="display: contents">
      <span data-slot="dropdown-menu-trigger" data-as-child style="display: contents">
        <button
          type="button"
          class="ui-sidebar-menu-button mx-switcher"
          data-slot="sidebar-menu-button"
          data-size="lg"
          data-active="false"
          title="${project ?? 'Choose a project'}"
        >
          <span class="mx-switcher__mark">${MatrixLogo({ className: 'mx-switcher__logo' })}</span>
          <span class="mx-switcher__text">
            <span class="mx-switcher__name">${parts === null ? 'Matrix' : parts.name}</span>
            <span class="mx-switcher__owner">${parts === null ? 'Choose a project' : parts.owner === '' ? 'Project' : parts.owner}</span>
          </span>
          ${Icon(GLYPH.chevronsUpDown)}
        </button>
      </span>
      <div
        class="ui-dropdown-menu-content mx-menu"
        data-slot="dropdown-menu-content"
        role="menu"
        hidden
        data-prefer-side="bottom"
        data-prefer-align="start"
        data-side-offset="6"
      >
        <div class="ui-dropdown-menu-label" data-slot="dropdown-menu-label">Projects</div>
        ${repeat(entries, (entry) => entry.scope, ProjectItem)}
        <div class="ui-dropdown-menu-separator" data-slot="dropdown-menu-separator" role="separator"></div>
        <div class="ui-dropdown-menu-item" data-slot="dropdown-menu-item" role="menuitem" tabindex="-1" data-href="${PROJECTS_PATH}">All projects</div>
      </div>
    </div>
  `;
}

/** The theme choices, in the order the kit's `ThemePreference` names them; checked state is the chrome's. */
const THEMES: ReadonlyArray<{ value: 'light' | 'dark' | 'system'; label: string }> = [
  { value: 'light', label: 'Light' },
  { value: 'dark', label: 'Dark' },
  { value: 'system', label: 'System' },
];

function ThemeItem(theme: (typeof THEMES)[number]): TemplateInstance {
  return html`
    <div class="ui-dropdown-menu-radio-item" data-slot="dropdown-menu-radio-item" role="menuitemradio" tabindex="-1" aria-checked="false" data-state="unchecked" data-value="${theme.value}">
      <span class="ui-dropdown-menu-item-indicator" data-slot="dropdown-menu-item-indicator"><span data-menu-item-indicator hidden><svg class="mx-icon" viewBox="0 0 16 16" aria-hidden="true"><circle cx="8" cy="8" r="2.5" fill="currentColor" stroke="none"></circle></svg></span></span>
      ${theme.label}
    </div>
  `;
}

function UserMenu(account: SignedInAccount | null): TemplateInstance {
  const identity = account ? accountIdentity(account) : { name: 'Signed in', email: '', initials: 'MX' };
  return html`
    <div class="ui-dropdown-menu" data-slot="dropdown-menu" data-menu="user" style="display: contents">
      <span data-slot="dropdown-menu-trigger" data-as-child style="display: contents">
        <button
          type="button"
          class="ui-sidebar-menu-button mx-user"
          data-slot="sidebar-menu-button"
          data-size="lg"
          data-active="false"
          title="${identity.email.length > 0 ? identity.email : identity.name}"
        >
          <span class="ui-avatar mx-user__avatar" data-slot="avatar" data-size="default"><span class="ui-avatar-fallback" data-slot="avatar-fallback">${identity.initials}</span></span>
          <span class="mx-switcher__text">
            <span class="mx-switcher__name">${identity.name}</span>
            <span class="mx-switcher__owner">${identity.email}</span>
          </span>
          ${Icon(GLYPH.chevronsUpDown)}
        </button>
      </span>
      <div
        class="ui-dropdown-menu-content mx-menu"
        data-slot="dropdown-menu-content"
        role="menu"
        hidden
        data-prefer-side="right"
        data-prefer-align="end"
        data-side-offset="8"
      >
        <div class="ui-dropdown-menu-label mx-menu__who" data-slot="dropdown-menu-label">
          <span class="ui-avatar mx-user__avatar" data-slot="avatar" data-size="default" aria-hidden="true"><span class="ui-avatar-fallback" data-slot="avatar-fallback">${identity.initials}</span></span>
          <span class="mx-menu__who-text">
            <span class="mx-menu__who-name">${identity.name}</span>
            <span class="mx-menu__who-email">${identity.email}</span>
          </span>
        </div>
        <div class="ui-dropdown-menu-separator" data-slot="dropdown-menu-separator" role="separator"></div>
        <div class="ui-dropdown-menu-item" data-slot="dropdown-menu-item" role="menuitem" tabindex="-1" data-href="${PROFILE_PATH}">${Icon(GLYPH.user)}Profile</div>
        <div class="ui-dropdown-menu-item" data-slot="dropdown-menu-item" role="menuitem" tabindex="-1" data-href="${SETTINGS_PATH}">${Icon(navGlyph('settings'))}Account settings</div>
        <div class="ui-dropdown-menu-sub" data-slot="dropdown-menu-sub" style="display: contents">
          <div class="ui-dropdown-menu-sub-trigger" data-slot="dropdown-menu-sub-trigger" role="menuitem" tabindex="-1" aria-haspopup="menu" aria-expanded="false" data-state="closed">${Icon(GLYPH.sun)}Theme<span class="mx-menu__more">${Icon(GLYPH.chevronRight)}</span></div>
          <div class="ui-dropdown-menu-sub-content" data-slot="dropdown-menu-sub-content" role="menu" hidden>
            <div class="ui-dropdown-menu-radio-group" data-slot="dropdown-menu-radio-group" role="group" aria-label="Theme" data-group="theme">
              ${repeat(THEMES, (theme) => theme.value, ThemeItem)}
            </div>
          </div>
        </div>
        <div class="ui-dropdown-menu-item" data-slot="dropdown-menu-item" role="menuitem" tabindex="-1" data-href="${LANDING_PATH}">${Icon(GLYPH.home)}matrixbased.com</div>
        <div class="ui-dropdown-menu-separator" data-slot="dropdown-menu-separator" role="separator"></div>
        <div class="ui-dropdown-menu-item" data-slot="dropdown-menu-item" role="menuitem" tabindex="-1" data-action="signout">${Icon(GLYPH.logout)}Sign out</div>
      </div>
    </div>
  `;
}

function Sidebar(options: ShellOptions): TemplateInstance {
  const directory = options.directory ?? null;
  return html`
    <div
      class="ui-sidebar group peer"
      data-slot="sidebar"
      data-state="expanded"
      data-collapsible=""
      data-collapsible-mode="icon"
      data-variant="sidebar"
      data-side="left"
      data-region="sidebar"
    >
      <div class="ui-sidebar-gap" data-slot="sidebar-gap"></div>
      <div class="ui-sidebar-container" data-slot="sidebar-container">
        <div class="ui-sidebar-inner" data-slot="sidebar-inner" data-sidebar="sidebar">
          <div class="ui-sidebar-header" data-slot="sidebar-header">
            <ul class="ui-sidebar-menu" data-slot="sidebar-menu">
              <li class="ui-sidebar-menu-item" data-slot="sidebar-menu-item">${ProjectSwitcher({ snapshot: options.snapshot, directory })}</li>
            </ul>
          </div>
          <nav class="ui-sidebar-content" data-slot="sidebar-content" aria-label="Primary navigation">
            <div class="ui-sidebar-group" data-slot="sidebar-group">
              <div class="ui-sidebar-group-label" data-slot="sidebar-group-label">Platform</div>
              <ul class="ui-sidebar-menu" data-slot="sidebar-menu">
                ${repeat(globalRows(options.path), (row) => row.key, MenuLink)}
              </ul>
            </div>
            <div class="ui-sidebar-group" data-slot="sidebar-group" hidden="${currentProject() === null}">
              <div class="ui-sidebar-group-label" data-slot="sidebar-group-label">Project</div>
              <ul class="ui-sidebar-menu" data-slot="sidebar-menu">
                ${repeat(projectRows(options.path), (row) => row.key, MenuLink)}
              </ul>
            </div>
          </nav>
          <div class="ui-sidebar-footer" data-slot="sidebar-footer">
            <ul class="ui-sidebar-menu" data-slot="sidebar-menu">
              <li class="ui-sidebar-menu-item" data-slot="sidebar-menu-item">${UserMenu(options.account ?? null)}</li>
            </ul>
          </div>
        </div>
      </div>
      <button type="button" class="ui-sidebar-rail" data-slot="sidebar-rail" aria-label="Toggle sidebar" tabindex="-1" title="Toggle sidebar"></button>
    </div>
  `;
}

type Crumb = { key: string; label: string; href: string | null };

/** The trail above the page: where in Matrix this address is. Every crumb but the last is a link. */
export function breadcrumbs(path: string): Crumb[] {
  const route = parseProjectPath(path);
  if (route !== null) {
    const tab = PROJECT_TABS.find((item) => item.tab === route.tab);
    const trail: Crumb[] = [
      { key: 'projects', label: 'Projects', href: PROJECTS_PATH },
      { key: 'project', label: route.project, href: projectHref('overview', {}, route.project) },
    ];
    if (route.changeId !== null) {
      trail.push(
        { key: 'changes', label: 'Changes', href: projectHref('changes', {}, route.project) },
        { key: 'change', label: short(route.changeId, 12), href: null },
      );
    } else if (route.sub === 'history') {
      trail.push(
        { key: 'code', label: 'Code', href: projectHref('code', {}, route.project) },
        { key: 'history', label: 'History', href: null },
      );
    } else {
      trail.push({ key: `tab:${route.tab}`, label: tab?.label ?? 'Overview', href: null });
    }
    return trail;
  }
  if (path === REPLAY_PATH) {
    const project = currentProject();
    return project === null
      ? [{ key: 'replay', label: 'Replay receipt', href: null }]
      : [
          { key: 'projects', label: 'Projects', href: PROJECTS_PATH },
          { key: 'project', label: project, href: projectHref('overview', {}, project) },
          { key: 'checks', label: 'Checks', href: projectHref('checks', {}, project) },
          { key: 'replay', label: 'Replay receipt', href: null },
        ];
  }
  const global = NAV.find((item) => item.href === path);
  return [{ key: path, label: global?.label ?? 'Matrix', href: null }];
}

function CrumbItem(crumb: Crumb & { first: boolean }): TemplateInstance {
  return html`
    <li class="ui-breadcrumb-item mx-crumb" data-slot="breadcrumb-item">
      ${crumb.first
        ? html``
        : html`<span class="ui-breadcrumb-separator" data-slot="breadcrumb-separator" role="presentation" aria-hidden="true"><svg viewBox="0 0 16 16"><path d="${GLYPH.chevronRight}"></path></svg></span>`}
      ${crumb.href === null
        ? html`<span class="ui-breadcrumb-page" data-slot="breadcrumb-page" aria-current="page">${crumb.label}</span>`
        : html`<a class="ui-breadcrumb-link" data-slot="breadcrumb-link" href="${crumb.href}" data-nav="${crumb.href}">${crumb.label}</a>`}
    </li>
  `;
}

const STATE_WORD: Record<AuthorityState, string> = {
  operational: 'Operational',
  empty: 'No certified state',
  attention: 'Needs attention',
  stale: 'Not live',
  unknown: 'Connecting',
};

function TopBar(options: ShellOptions): TemplateInstance {
  const state = authorityState(options.snapshot, options.connectionState);
  // A read that failed or never answered is no longer "connecting": say it is not there, in the muted tone.
  const word =
    options.unavailable === true && options.snapshot === null
      ? { state: 'unavailable', word: 'Unavailable', title: 'Project data is not available' }
      : { state, word: STATE_WORD[state], title: authorityStateLabel(state) };
  const trail = breadcrumbs(options.path).map((crumb, index) => ({ ...crumb, first: index === 0 }));
  const checks = projectHref('checks');
  return html`
    <header class="mx-topbar" data-region="header">
      <button type="button" class="ui-button ui-sidebar-trigger" data-slot="sidebar-trigger" data-variant="ghost" data-size="icon-sm" aria-label="Toggle sidebar" title="Toggle sidebar">${Icon(GLYPH.panel)}</button>
      <div class="ui-separator mx-topbar__rule" data-slot="separator" data-orientation="vertical" role="none"></div>
      <nav class="ui-breadcrumb mx-topbar__crumbs" data-slot="breadcrumb" aria-label="Breadcrumb">
        <ol class="ui-breadcrumb-list" data-slot="breadcrumb-list">${repeat(trail, (crumb) => crumb.key, CrumbItem)}</ol>
      </nav>
      <div class="mx-topbar__end">
        <button type="button" class="ui-button mx-search" data-slot="button" data-variant="outline" data-size="sm" data-action="palette" aria-label="Search Matrix" title="Search Matrix">
          ${Icon(navGlyph('search'))}<span class="mx-search__label">Search…</span>
          <kbd class="ui-kbd mx-search__kbd" data-slot="kbd">⌘K</kbd>
        </button>
        <a class="ui-badge mx-status" data-slot="badge" data-variant="outline" data-state="${word.state}" href="${checks}" data-nav="${checks}" title="${word.title}" aria-label="${word.title}">
          <i aria-hidden="true"></i><span>${word.word}</span>
        </a>
      </div>
    </header>
  `;
}

/** A failed read, or a project the authority has nothing for: the kit's one answer. */
function bodyOf(options: ShellOptions): TemplateInstance {
  if (options.error === null || options.error === '') return options.body;
  // Only a refusal of membership has a cause of its own to say; every other failure is "not published here".
  return DataUnavailable({ detail: options.error === NO_PROJECT_MESSAGE ? options.error : undefined });
}

/** Renders the signed-in frame around one route's body. */
export function Shell(options: ShellOptions): TemplateInstance {
  return html`
    <div class="ui-sidebar-wrapper mx-shell" data-slot="sidebar-wrapper" data-connection="${options.connectionState}" style="--sidebar-width:15rem;--sidebar-width-icon:3.25rem">
      <a class="mx-skip" href="#matrix-content">Skip to content</a>
      ${Sidebar(options)}
      <main id="matrix-content" class="ui-sidebar-inset mx-inset min-w-0" data-slot="sidebar-inset" tabindex="-1">
        ${TopBar(options)}
        <div class="mx-content" data-region="content">${bodyOf(options)}</div>
      </main>
    </div>
  `;
}
